import qs from 'qs';
import { useLocation, useNavigate } from 'react-router-dom';

import { urlQueryStringify } from '../helpers/routes';

const useReactRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const setQuery = (query: object, saveCurrentQuery?: boolean) => {
    let newQuery = query;
    if (saveCurrentQuery) {
      const currentQuery = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      });
      newQuery = { ...currentQuery, ...newQuery };
    }

    let searchQuery = '';
    if (newQuery && !!Object.keys(newQuery)) {
      searchQuery = `?${urlQueryStringify(newQuery)}`;
    }

    navigate(`${location.pathname}${searchQuery}`, { replace: true });
  };

  function getUrlQueryParams<T>(): T {
    const parsedQueryString = qs.parse(location.search, {
      decoder(value) {
        if (/^(\d+|\d*\.\d+)$/.test(value)) {
          return parseFloat(value);
        }

        const keywords = {
          true: true,
          false: false,
          null: null,
          undefined,
        };

        if (value in keywords) {
          return keywords[value as keyof typeof keywords];
        }

        return decodeURI(value);
      },
      ignoreQueryPrefix: true,
    });

    return Object.keys(parsedQueryString).reduce((params, key: string) => {
      // normalize boolean values
      if (parsedQueryString[key] === 'true' || parsedQueryString[key] === 'false') {
        return { ...params, [key]: parsedQueryString[key] === 'true' };
      }

      return {
        ...params,
        [key]: parsedQueryString[key],
      };
    }, {} as T);
  }

  const getNavigationUrl = (pathname: string, query?: object, saveCurrentQuery?: boolean) => {
    let queryData: object | null = null;

    if (query) {
      queryData = { ...query };
    }

    if (saveCurrentQuery) {
      const parsedQueryString = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      });
      queryData = { ...parsedQueryString, ...(queryData ? queryData : {}) };
    }

    let searchQuery = '';
    if (queryData && !!Object.keys(queryData)) {
      searchQuery = `?${urlQueryStringify(queryData)}`;
    }

    return `${pathname}${searchQuery}`;
  };

  const navigateTo = (
    pathname: string,
    query?: object,
    saveCurrentQuery?: boolean,
    state?: string
  ) => {
    const navigationUrl = getNavigationUrl(pathname, query, saveCurrentQuery);

    navigate(navigationUrl, {
      state,
    });
  };

  return { setQuery, navigateTo, getUrlQueryParams, urlQueryStringify, getNavigationUrl };
};

export default useReactRouter;
